import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    ComposedChart,
    Line,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Cell
} from 'recharts';
import styles from './DrugResponsePlot_Fixed.module.css';
import html2canvas from 'html2canvas';

// ==========================================
// [Zone 1] Analysis Logic (Math Helpers)
// ==========================================

function matrixMultiply(a, b) {
    const result = new Array(a.length).fill().map(() => new Array(b[0].length).fill(0));
    for (let i = 0; i < a.length; i++) {
        for (let j = 0; j < b[0].length; j++) {
            for (let k = 0; k < b.length; k++) {
                result[i][j] += a[i][k] * b[k][j];
            }
        }
    }
    return result;
}

function transpose(matrix) {
    return matrix[0].map((_, i) => matrix.map(row => row[i]));
}

function solveSystem(A, b) {
    const n = A.length;
    const augmented = A.map((row, i) => [...row, b[i]]);
    for (let i = 0; i < n; i++) {
        let maxRow = i;
        for (let j = i + 1; j < n; j++) {
            if (Math.abs(augmented[j][i]) > Math.abs(augmented[maxRow][i])) {
                maxRow = j;
            }
        }
        [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];
        for (let j = i + 1; j < n; j++) {
            const factor = augmented[j][i] / augmented[i][i];
            for (let k = i; k <= n; k++) {
                augmented[j][k] -= factor * augmented[i][k];
            }
        }
    }
    const x = new Array(n).fill(0);
    for (let i = n - 1; i >= 0; i--) {
        x[i] = augmented[i][n];
        for (let j = i + 1; j < n; j++) {
            x[i] -= augmented[i][j] * x[j];
        }
        x[i] /= augmented[i][i];
    }
    return x;
}

function computeJacobian(x, params, model) {
    const h = 1e-8;
    const n = x.length;
    const p = params.length;
    const J = new Array(n).fill().map(() => new Array(p).fill(0));
    for (let j = 0; j < p; j++) {
        const paramsPlusH = [...params];
        paramsPlusH[j] += h;
        const paramsMinusH = [...params];
        paramsMinusH[j] -= h;
        for (let i = 0; i < n; i++) {
            J[i][j] = (model(x[i], ...paramsPlusH) - model(x[i], ...paramsMinusH)) / (2 * h);
        }
    }
    return J;
}

function levenbergMarquardt(x, y, model, initialParams, maxIterations = 1000) {
    let params = [...initialParams];
    let iteration = 0;
    let prevChiSquare = Infinity;
    let damping = 0.1;
    while (iteration < maxIterations) {
        const predicted = x.map(xi => model(xi, ...params));
        const residuals = y.map((yi, i) => yi - predicted[i]);
        const chiSquare = residuals.reduce((sum, r) => sum + r * r, 0);
        if (Math.abs(chiSquare - prevChiSquare) < 1e-8 && iteration > 100) {
            break;
        }
        const J = computeJacobian(x, params, model);
        const JT = transpose(J);
        const JTJ = matrixMultiply(JT, J.map(row => [row]).map(col => col[0]));
        const JTr = matrixMultiply(JT, residuals.map(r => [r])).map(r => r[0]);
        const dampedJTJ = JTJ.map((row, i) =>
            row.map((val, j) => val + (i === j ? damping * (1 + Math.abs(val)) : 0))
        );
        try {
            const delta = solveSystem(dampedJTJ, JTr);
            const newParams = params.map((p, i) => p + delta[i]);
            const newPredicted = x.map(xi => model(xi, ...newParams));
            const newResiduals = y.map((yi, i) => yi - newPredicted[i]);
            const newChiSquare = newResiduals.reduce((sum, r) => sum + r * r, 0);
            if (newChiSquare < chiSquare) {
                params = newParams;
                damping = Math.max(damping * 0.1, 1e-7);
                prevChiSquare = chiSquare;
            } else {
                damping = Math.min(damping * 10, 1e7);
            }
        } catch (e) {
            damping = Math.min(damping * 10, 1e7);
        }
        iteration++;
    }
    return params;
}

// ==========================================
// [Zone 2] Visualization & Component Logic
// ==========================================

const colors = [
    "#e6194b", "#3cb44b", "#ffe119", "#4363d8", "#f58231",
    "#911eb4", "#46f0f0", "#f032e6", "#bcf60c", "#fabebe",
    "#008080", "#e6beff", "#9a6324", "#fffac8", "#800000",
    "#aaffc3", "#808000", "#ffd8b1", "#000075", "#808080"
];

// ... 기존 코드 (colors 배열 등)

// 🌟 1. 레전드 텍스트 포매터 추가 (IC50 -> IC + 50 아래첨자)
// 🌟 1. 레전드 텍스트 포매터 수정 (글자색 복구)
const renderLegendText = (value, entry) => {
    const { color } = entry; // 👈 Recharts가 넘겨주는 선 색상을 가져옵니다.

    if (typeof value === 'string' && value.includes('IC50')) {
        const parts = value.split('IC50');
        return (
            <span style={{ color: color }}> {/* 👈 가져온 색상을 적용! */}
                {parts[0]}IC<sub>50</sub>{parts[1]}
            </span>
        );
    }
    return <span style={{ color: color }}>{value}</span>;
};

// 🌟 2. 툴팁: Raw 데이터는 숨기고, 계산된 Line 값만 보여주며 IC50 아래첨자 적용
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className={styles.customTooltip} style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                <p className={styles.tooltipLabel} style={{ margin: 0, fontWeight: 'bold' }}>
                    {`Log Conc: ${typeof label === 'number' ? label.toFixed(2) : label}`}
                </p>
                {payload.map((pld, index) => {
                    // Raw 데이터 제외
                    if (pld.dataKey && pld.dataKey.includes('_raw')) return null;
                    if (pld.name && pld.name.includes('(Raw)')) return null;

                    // 툴팁에서도 IC50 텍스트를 찾아 아래첨자로 변환
                    const formattedName = pld.name.includes('IC50') ? (
                        <span>{pld.name.split('IC50')[0]}IC<sub>50</sub>{pld.name.split('IC50')[1]}</span>
                    ) : (
                        pld.name
                    );

                    return (
                        <div key={index} style={{ color: pld.color, marginTop: '4px', fontSize: '13px' }}>
                            {formattedName}: {typeof pld.value === 'number' ? pld.value.toFixed(1) : pld.value}
                        </div>
                    );
                })}
            </div>
        );
    }
    return null;
};

// ... 기존 AxisLabelTwoLines 등 유지

const AxisLabelTwoLines = ({ viewBox, line1, line2 }) => {
    const { x, y, width, height } = viewBox;
    const cx = x - 20;
    const cy = y + height / 2;

    return (
        <text x={cx} y={cy} textAnchor="middle" transform={`rotate(-90, ${cx}, ${cy})`}>
            <tspan x={cx} dy="-0.5em" style={{ fontSize: '12px', fontWeight: 'bold', fill: '#666' }}>{line1}</tspan>
            <tspan x={cx} dy="1.2em" style={{ fontSize: '11px', fill: '#666' }}>{line2}</tspan>
        </text>
    );
};

const DoseResponseCurve = ({ data, setGraphImage }) => {
    const { tmpKey } = useParams();
    const chartContainerRef = useRef(null);
    const [chartSize, setChartSize] = useState({ width: 800, height: 600 });
    const isSmallScreen = chartSize.width < 768;

    // 통합된 하나의 데이터 배열 (Wide Format)
    const [mergedData, setMergedData] = useState([]);

    // 키 값 매핑 저장소 (originalKey -> safeKey)
    const [keyMap, setKeyMap] = useState([]); // [{ original: 'Drug#1', safe: 'series_0', color: ... }, ...]
    const [ic50Values, setIC50Values] = useState({});

    // 모달 관련
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingValues, setEditingValues] = useState({});

    useEffect(() => {
        const handleResize = () => {
            if (chartContainerRef.current) {
                const { clientWidth, clientHeight } = chartContainerRef.current;
                setChartSize({ width: clientWidth, height: clientHeight });
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!data) return;

        const processData = async () => {
            const originalKeys = Object.keys(data);
            const ic50List = {};
            const fitResults = {}; // { series_0: { model, params, curveMin, curveMax }, ... }

            // 1. 키 매핑 생성 (특수문자 문제 원천 봉쇄)
            const mappedKeys = originalKeys.map((key, index) => ({
                original: key,
                safeLine: `series_${index}_line`, // 곡선용 키
                safeRaw: `series_${index}_raw`,   // 점(Raw)용 키
                color: colors[index % colors.length]
            }));
            setKeyMap(mappedKeys);

            // 2. 전체 X축 포인트 수집 (Scatter가 찍힌 모든 점 + 100등분 점)
            let allXPoints = new Set();
            let globalMin = Infinity;
            let globalMax = -Infinity;

            // (1) 실제 데이터 포인트 수집
            originalKeys.forEach(key => {
                const xdata = data[key].xdata;
                xdata.forEach(x => {
                    allXPoints.add(parseFloat(x.toFixed(3)));
                    if (x < globalMin) globalMin = x;
                    if (x > globalMax) globalMax = x;
                });
            });

            // 데이터가 없을 경우 기본값
            if (globalMin === Infinity) { globalMin = 0; globalMax = 5; }

            // (2) 100등분 포인트 추가 (부드러운 곡선을 위해)
            const step = (globalMax - globalMin) / 100;
            for (let i = 0; i <= 100; i++) {
                allXPoints.add(parseFloat((globalMin + step * i).toFixed(3)));
            }

            // (3) 정렬된 유니크 X 배열 생성
            const sortedX = Array.from(allXPoints).sort((a, b) => a - b);

            // 3. 각 약물별 피팅(Fitting) 및 IC50 계산
            const promises = mappedKeys.map(async (kMap) => {
                const key = kMap.original;
                const xdata = data[key].xdata;
                const ydata = data[key].ydata.map(y => Math.max(y, 0));

                try {
                    // Backend API
                    const res = await fetch("/api/calculateIC50", {
                        method: "post",
                        headers: { "content-type": "application/json" },
                        body: JSON.stringify({
                            xdata, ydata, sampleKey: tmpKey, drugName: key.replace(/hashmark/g, '#')
                        })
                    });
                    const resData = await res.json();

                    if (resData) {
                        const rawVal = String(resData.IC50);
                        ic50List[key] = rawVal.includes("IC50") ? rawVal : `IC50: ${rawVal}`;
                    }

                    // 4PL Fitting
                    const model = function (x, top, bottom, logIC50) {
                        const hillSlope = -1;
                        return bottom + (top - bottom) / (1 + Math.pow(10, hillSlope * (x - logIC50)));
                    };
                    const initialParams = [0, 100, resData?.logIC50 || 1.5];
                    const fitted = levenbergMarquardt(xdata, ydata, model, initialParams);

                    // 피팅 결과 저장
                    fitResults[kMap.safeLine] = { model, fitted, xdata, ydata };

                    // IC50 텍스트 보정 (Min/Max 체크용 임시 계산)
                    let localMinY = Infinity, localMaxY = -Infinity;
                    sortedX.forEach(x => {
                        const y = Math.max(model(x, ...fitted), 0);
                        if (y < localMinY) localMinY = y;
                        if (y > localMaxY) localMaxY = y;
                    });

                    if (localMinY > 50) ic50List[key] = "IC50 > 10,000 nM";
                    else if (localMaxY < 50) ic50List[key] = "IC50 < 3.2 nM";

                } catch (e) {
                    console.error(e);
                }
            });

            await Promise.all(promises);

            // 4. [핵심] 통합 데이터 배열(Wide Format) 생성
            // 구조: [ { x: 0.1, series_0_line: 90, series_0_raw: 92, series_1_line: 50, ... }, ... ]
            const finalData = sortedX.map(xVal => {
                const row = { x: xVal };

                mappedKeys.forEach(kMap => {
                    const fitInfo = fitResults[kMap.safeLine];
                    if (fitInfo) {
                        // 1) Line 값: 모든 X 좌표에 대해 무조건 계산 (끊김 없음, 툴팁 항상 표시)
                        const lineY = Math.max(fitInfo.model(xVal, ...fitInfo.fitted), 0);
                        row[kMap.safeLine] = lineY;

                        // 2) Raw 값: 실제 데이터 포인트와 일치하는 경우에만 값 할당
                        // 소수점 오차 고려하여 비교
                        const rawIndex = fitInfo.xdata.findIndex(rawX => Math.abs(rawX - xVal) < 0.0001);
                        if (rawIndex !== -1) {
                            // Raw 데이터가 여러 개일 수 있으므로 평균을 낼 수도 있으나, 여기선 해당 위치 값 사용
                            // (processData 초입에서 group 처리를 하면 더 정확하지만, 로직 단순화를 위해 직접 매핑)
                            // 여기서는 기존 로직의 평균내기를 대체하여, 해당 x에 대한 y값들의 평균을 구함
                            const matchingY = fitInfo.ydata.filter((_, i) => Math.abs(fitInfo.xdata[i] - xVal) < 0.0001);
                            const avgY = matchingY.reduce((a, b) => a + b, 0) / matchingY.length;
                            row[kMap.safeRaw] = avgY;
                        }
                    }
                });
                return row;
            });

            setMergedData(finalData);
            setIC50Values(ic50List);
            setEditingValues(ic50List);
        };

        processData();
    }, [data]);

    useEffect(() => {
        if (mergedData.length > 0 && setGraphImage && chartContainerRef.current) {
            const timer = setTimeout(() => {
                html2canvas(chartContainerRef.current, { backgroundColor: "#ffffff", scale: 2 })
                    .then(canvas => setGraphImage(canvas.toDataURL('image/png')));
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [mergedData, setGraphImage, ic50Values]);

    // Modal Handlers
    const handleOpenEditModal = () => { setEditingValues({ ...ic50Values }); setShowEditModal(true); };
    const handleInputChange = (key, value) => { setEditingValues(prev => ({ ...prev, [key]: value })); };
    const handleSaveIC50 = async () => {
        try {
            const res = await fetch('/api/updateIC50', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sampleKey: tmpKey, updates: editingValues })
            });
            if (!res.ok) throw new Error('Fail');
            setIC50Values(editingValues); setShowEditModal(false); alert("Success");
        } catch (e) { alert("Failed"); }
    };

    const dynamicMargin = isSmallScreen ? { top: 20, right: 20, left: 20, bottom: 60 } : { top: 20, right: 150, left: 50, bottom: 40 };

    return (
        <div ref={chartContainerRef} className={styles.chartContainer} style={{ position: 'relative' }}>
            <div className={styles.headerContainer} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                <h2 className={styles.title} style={{ marginRight: '15px', marginBottom: 0 }}>
                    {tmpKey ? tmpKey.replaceAll('hashmark', '#') : ''}
                </h2>
                <button onClick={handleOpenEditModal} style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: '#f0f0f0', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>Edit IC50</button>
            </div>

            <ComposedChart
                width={chartSize.width * 0.95}
                height={Math.max(chartSize.height * 0.85, 500)}
                margin={dynamicMargin}
                data={mergedData} // [중요] 모든 데이터가 하나로 합쳐진 배열 사용
            >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="x" type="number" domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} allowDataOverflow={true} label={{ value: 'Drug concentration(log, nmol/L)', position: 'bottom', offset: 0 }} tickFormatter={(tick) => tick.toFixed(0)} />
                <YAxis domain={[0, 150]} ticks={[0, 50, 100, 150]} allowDataOverflow={true} label={<AxisLabelTwoLines line1="Relative cell viability" line2="(% control)" />} tickFormatter={(tick) => tick.toFixed(0)} />
                <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
                <Legend
                    iconSize={14}
                    wrapperStyle={isSmallScreen ? { fontSize: '12px' } : { right: 0, top: '50%', transform: 'translateY(-50%)' }}
                    layout={isSmallScreen ? 'horizontal' : 'vertical'}
                    verticalAlign={isSmallScreen ? 'bottom' : 'middle'}
                    align={isSmallScreen ? 'center' : 'right'}
                    formatter={renderLegendText} /* 👈 여기에 포매터 추가! */
                />

                {keyMap.map((kMap) => {
                    const groupName = kMap.original.replace(/hashmark/g, '#');
                    const ic50Text = ic50Values[kMap.original] || '';

                    return (
                        <React.Fragment key={kMap.original}>
                            {/* Line: 계산된 Curve 데이터 (safeLine 키 사용) */}
                            <Line
                                dataKey={kMap.safeLine}
                                name={`${groupName} (${ic50Text})`}
                                type="monotone"
                                stroke={kMap.color}
                                strokeWidth={3}
                                dot={false}
                                isAnimationActive={false}
                                connectNulls={true}
                                legendType="plainline" /* 👈 이걸 추가하면 빈 구멍 없이 깔끔한 직선 모양이 됩니다! */
                            />
                            {/* Scatter: 실제 포인트 데이터 (safeRaw 키 사용) */}
                            <Scatter
                                name={`${groupName} (Raw)`}
                                dataKey={kMap.safeRaw}
                                fill={kMap.color}
                                legendType="none"
                            >
                                {mergedData.filter(d => d[kMap.safeRaw] !== undefined).map((entry, i) => (
                                    <Cell key={`cell-${i}`} fill={kMap.color} stroke="#ffffff" strokeWidth={2} r={6} />
                                ))}
                            </Scatter>
                        </React.Fragment>
                    );
                })}
            </ComposedChart>

            {showEditModal && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', width: '400px', maxHeight: '80%', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Edit IC50 Values</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {keyMap.map((kMap) => {
                                const drugName = kMap.original.replace(/hashmark/g, '#');
                                return (
                                    <div key={kMap.original} style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>{drugName}</label>
                                        <input type="text" value={editingValues[kMap.original] || ''} onChange={(e) => handleInputChange(kMap.original, e.target.value)} style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button onClick={() => setShowEditModal(false)} style={{ padding: '8px 15px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={handleSaveIC50} style={{ padding: '8px 15px', border: 'none', borderRadius: '4px', backgroundColor: '#4363d8', color: 'white', cursor: 'pointer' }}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DoseResponseCurve;